import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Modal, Box } from "@mui/material";
import api from "../../../../api/api";
import "../../../../components/table.css";
import ViewImg from "../../../../image/View.svg";
import Button from "../../../../components/Button";
import BackButton from "../../../../components/BackButton";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import BadgeIcon from "@mui/icons-material/Badge";
import BusinessIcon from "@mui/icons-material/Business";
import "./ListOfUploadDocuments.css";
import Badge from "../../../../components/Badge";
 
const ListOfUploadedDocuments = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [gstDetails, setGstDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [isConfirmed, setIsConfirmed] = useState(false);
 
  useEffect(() => {
    const fetchData = async () => {
      console.log("Fetching documents and GST details for userId:", userId);
      try {
        const docsResponse = await api.get(`/user-documents/${userId}`);
        console.log("Documents API response:", docsResponse.data);
        setDocuments(
          Array.isArray(docsResponse.data.documents)
            ? docsResponse.data.documents
            : []
        );
 
        const gstResponse = await api.get(`/gst-details/${userId}`);
        console.log("GST API response:", gstResponse.data);
        setGstDetails(gstResponse.data);
      } catch (error) {
        console.error("Failed to fetch data:", error);
        setDocuments([]);
        setGstDetails(null);
      } finally {
        setLoading(false);
      }
    };
 
    fetchData();
  }, [userId]);
 
  const handleVerifyClick = () => {
    setShowConfirmModal(true);
    setIsConfirmed(false);
  };
 
  const handleConfirmVerify = async () => {
    setShowConfirmModal(false);
    setVerifying(true);
    try {
      await api.post(`/verify-user/${userId}`);
      setSuccessMessage("User verified successfully!");
      setShowSuccessModal(true);
    } catch (error) {
      console.error("Failed to verify user:", error);
      alert("Failed to verify user. Please try again.");
    } finally {
      setVerifying(false);
    }
  };
 
  const handleCancelVerify = () => {
    setShowConfirmModal(false);
    setIsConfirmed(false);
  };
 
  const handleRejectClick = () => {
    setShowRejectModal(true);
  };
 
  const handleConfirmReject = async () => {
    setShowRejectModal(false);
    setRejecting(true);
    try {
      await api.post(`/reject-user/${userId}`);
      setSuccessMessage("User rejected successfully!");
      setShowSuccessModal(true);
    } catch (error) {
      console.error("Failed to reject user:", error);
      alert("Failed to reject user. Please try again.");
    } finally {
      setRejecting(false);
    }
  };
 
  const handleCancelReject = () => {
    setShowRejectModal(false);
  };
 
  return (
    <>
      <BackButton />
      {loading ? (
        <p>Loading...</p>
      ) : (
        <>
          {gstDetails && (
            <div className="mb-4">
              <div className="bg-light rounded p-3 mb-3">
                <div className="row align-items-center">
                  <div className="col-md-8">
                    <h5 className="head">{gstDetails.user.company_name}</h5>
                    <p className="mb-1 text-muted small d-flex flex-column flex-sm-row align-items-sm-center gap-2">
                      <span className="d-flex align-items-center">
                        <PersonIcon fontSize="small" className="me-1" />
                        {gstDetails.user.full_name}
                      </span>
 
                      <span className="d-none d-sm-inline">|</span>
 
                      <span className="d-flex align-items-center">
                        <EmailIcon fontSize="small" className="me-1" />
                        {gstDetails.user.email}
                      </span>
 
                      <span className="d-none d-sm-inline">|</span>
 
                      <span className="d-flex align-items-center">
                        <PhoneIcon fontSize="small" className="me-1" />
                        {gstDetails.user.phone_number}
                      </span>
                    </p>
                  </div>
                  <div className="col-md-4 text-end">
                    <div className="d-flex align-items-center justify-content-sm-end">
                      <Badge className="primary" extracls="custombadge">
                        <BadgeIcon fontSize="small" className="me-1" />
                        {gstDetails.user.gst_number}
                      </Badge>
                    </div>
                  </div>
                </div>
              </div>
 
              {gstDetails.gst_verification && (
                <div className="accordion" id="gstDetailsAccordion">
                  <div className="accordion-item">
                    <button
                      className="accordion-button noicon d-flex align-items-center justify-content-between box-shadow-none"
                      type="button"
                      data-bs-toggle="collapse"
                      data-bs-target="#gstVerificationCollapse"
                      aria-expanded="true"
                      aria-controls="gstVerificationCollapse"
                    >
                      <h3 className="head mb-0">
                        GST Verification Details
                        <span
                          className={`badge ms-2 ${
                            gstDetails.gst_verification.verification_response
                              ?.isValid
                              ? "bg-success"
                              : "bg-danger"
                          }`}
                        >
                          {gstDetails.gst_verification.verification_response
                            ?.isValid
                            ? "Valid"
                            : "Invalid"}
                        </span>
                      </h3>
                      <p className="mb-0 small">
                        <strong>Verified At:</strong>
                        <span className="ms-1">
                          {new Date(
                            gstDetails.gst_verification.verified_at
                          ).toLocaleString()}
                        </span>
                      </p>
                    </button>
                    <div
                      id="gstVerificationCollapse"
                      className="accordion-collapse collapse show"
                      aria-labelledby="gstVerificationHeading"
                    >
                      <div className="accordion-body">
                        <h5 className="customcardheading">
                          <BusinessIcon fontSize="small" className="me-2" />
                          GST Portal Information
                        </h5>
                        <div className="row mb-3 g-3">
                          <div className="col-md-6 d-flex">
                            <div className="customcard">
                              <div className="gst-info-list">
                                <div className="row">
                                  <div className="col-md-4 small text-muted">
                                    Legal Name:{" "}
                                  </div>
                                  <div className="col-md-8 small fw-medium">
                                    {
                                      gstDetails.gst_verification.gst_details
                                        ?.lgnm
                                    }
                                  </div>
                                </div>
                                <div className="row">
                                  <div className="col-5 col-md-4 small text-muted">
                                    Registration Date:
                                  </div>
                                  <div className="col-7 col-md-8 small fw-medium">
                                    {
                                      gstDetails.gst_verification.gst_details
                                        ?.rgdt
                                    }
                                  </div>
                                </div>
                                <h3 className="customcardheading fontsmall">
                                  Jurisdiction
                                </h3>
                                {gstDetails.gst_verification.gst_details?.stj &&
                                  gstDetails.gst_verification.gst_details.stj
                                    .split(",")
                                    .map((item, index) => {
                                      console.log("ok", item);
                                      const [label, value] = item.split(" - ");
                                      return (
                                        <div
                                          className="row noborder"
                                          key={index}
                                        >
                                          <div className="col-4 small text-muted bulletpoint position-relative">
                                            <span className=" ps-4">
                                              {label}:
                                            </span>
                                          </div>
                                          <div className="col-8 small fw-medium">
                                            {value}
                                          </div>
                                        </div>
                                      );
                                    })}
                              </div>
                            </div>
                          </div>
 
                          <div className="col-md-6 d-flex">
                            <div className="customcard">
                              <div className="gst-info-list">
                                <div className="row">
                                  <div className="col-md-4 small text-muted">
                                    Type:
                                  </div>
                                  <div className="col-md-8 small fw-medium">
                                    {
                                      gstDetails.gst_verification.gst_details
                                        ?.dty
                                    }
                                  </div>
                                </div>
                                <div className="row">
                                  <div className="col-md-4 small text-muted">
                                    Constitution:
                                  </div>
                                  <div className="col-md-8 small fw-medium">
                                    {
                                      gstDetails.gst_verification.gst_details
                                        ?.ctb
                                    }
                                  </div>
                                </div>
 
                                {/* Status */}
                                <div className="row">
                                  <div className="col-md-4 small text-muted">
                                    Status:
                                  </div>
                                  <div className="col-md-8 small fw-medium">
                                    {
                                      gstDetails.gst_verification.gst_details
                                        ?.sts
                                    }
                                  </div>
                                </div>
 
                                {/* Address */}
                                <div className="row">
                                  <div className="col-md-4 small text-muted">
                                    Address:
                                  </div>
                                  <div className="col-md-8 small fw-medium">
                                    {
                                      gstDetails.gst_verification.gst_details
                                        ?.pradr?.adr
                                    }
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
 
           <div className="table-responsive table-fixed-header overflow-auto mb-4">
            <table
              className="table table-bordered table-hover"
              style={{ tableLayout: "fixed" }}
            >
              <thead className="table-light">
                <tr>
                  <th>Document Type</th>
                  <th>Upload Date</th>
                  <th>View</th>
                </tr>
              </thead>
              <tbody>
                {documents.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="text-center">
                      No documents found
                    </td>
                  </tr>
                ) : (
                  documents.map((doc) => (
                    <tr key={doc.id}>
                      <td>{doc.document_type}</td>
                      <td>{new Date(doc.uploaded_at).toLocaleDateString()}</td>
                      <td>
                        <div
                          className="d-flex gap-2 justify-content-center imgintbl"
                          style={{ maxWidth: "100%" }}
                        >
                          <a
                            href={doc.file_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="View"
                          >
                            <img
                              src={ViewImg}
                              alt="View"
                              width={20}
                              height={20}
                            />
                          </a>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
 
          <div className="d-flex justify-content-end">
            <Button
              onClick={handleVerifyClick}
              text={verifying ? "Verifying..." : "Verify User"}
              btncls="me-2"
              disabled={verifying}
            />
            <Button
              onClick={handleRejectClick}
              text={rejecting ? "Rejecting..." : "Reject User"}
              variant="danger"
              btncls="me-2"
              disabled={rejecting}
            />
          </div>
        </>
      )}
 
      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          role="dialog"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm Verification</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCancelVerify}
                ></button>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to verify this user?</p>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="confirmCheckbox"
                    checked={isConfirmed}
                    onChange={(e) => setIsConfirmed(e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="confirmCheckbox">
                    I confirm that I have checked and verified all user documents.
                  </label>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCancelVerify}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleConfirmVerify}
                  disabled={!isConfirmed}
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
 
      {/* Reject Modal */}
      {showRejectModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          role="dialog"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm Rejection</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCancelReject}
                ></button>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to reject this user?</p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCancelReject}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleConfirmReject}
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
 
      {/* Success Modal */}
      <Modal open={showSuccessModal} onClose={() => setShowSuccessModal(false)}>
        <Box className="modal-box" style={{ height: "200px" }}>
          <h6 className="fw-bold">Success</h6>
          <p>{successMessage}</p>
          <div className="row mt-5 justify-content-center">
            <div className="col-6">
              <Button
                variant="primary"
                btncls="w-100"
                onClick={() => {
                  setShowSuccessModal(false);
                  navigate("/");
                }}
              >
                OK
              </Button>
            </div>
          </div>
        </Box>
      </Modal>
    </>
  );
};
 
export default ListOfUploadedDocuments;
 
 